import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { BillingFrequency } from '../lib/complianceRoll.js';
import type { SyncState } from './jobsDb.js';

export type ComplianceCategory =
  | 'insurance'
  | 'vehicle'
  | 'equipment'
  | 'training'
  | 'membership'
  | 'statutory'
  | 'other';

export type CompliancePendingOp = 'create' | 'update' | 'delete' | 'renew' | null;

export type LocalCompliance = {
  id: string;
  category: ComplianceCategory;
  title: string;
  subject_reference: string | null;
  issuer: string | null;
  issued_date: string | null;
  expiry_date: string | null;
  lead_time_days: number;
  cost_pence: number | null;
  billing_frequency: BillingFrequency;
  notes: string | null;
  document_reference: string | null;
  active: boolean;
  deleted_at: string | null;
  syncState: SyncState;
  pendingOp: CompliancePendingOp;
  /** For renew flush: optional one_off next expiry */
  renewNewExpiry: string | null;
  failReason: string | null;
  updatedAt: string;
};

interface ComplianceDB extends DBSchema {
  compliance: {
    key: string;
    value: LocalCompliance;
    indexes: { by_sync: SyncState };
  };
}

let dbPromise: Promise<IDBPDatabase<ComplianceDB>> | null = null;

function dateOnly(v: string | Date | null | undefined): string | null {
  if (v == null) return null;
  if (typeof v === 'string') return v.slice(0, 10);
  return v.toISOString().slice(0, 10);
}

export function normalizeCompliance(raw: LocalCompliance): LocalCompliance {
  return {
    ...raw,
    subject_reference: raw.subject_reference ?? null,
    issuer: raw.issuer ?? null,
    issued_date: dateOnly(raw.issued_date),
    expiry_date: dateOnly(raw.expiry_date),
    lead_time_days: raw.lead_time_days ?? 30,
    cost_pence: raw.cost_pence ?? null,
    billing_frequency: raw.billing_frequency ?? 'annual',
    notes: raw.notes ?? null,
    document_reference: raw.document_reference ?? null,
    active: raw.active !== false,
    deleted_at: raw.deleted_at ?? null,
    pendingOp: raw.pendingOp ?? null,
    renewNewExpiry: raw.renewNewExpiry ?? null,
    failReason: raw.failReason ?? null,
  };
}

export function getComplianceDb() {
  if (!dbPromise) {
    dbPromise = openDB<ComplianceDB>('terratech-compliance', 1, {
      upgrade(db) {
        const store = db.createObjectStore('compliance', { keyPath: 'id' });
        store.createIndex('by_sync', 'syncState');
      },
    });
  }
  return dbPromise;
}

export async function putComplianceLocal(item: LocalCompliance): Promise<void> {
  const db = await getComplianceDb();
  await db.put('compliance', normalizeCompliance(item));
}

export async function getCompliance(
  id: string,
): Promise<LocalCompliance | undefined> {
  const db = await getComplianceDb();
  const row = await db.get('compliance', id);
  return row ? normalizeCompliance(row) : undefined;
}

export async function listComplianceLocal(): Promise<LocalCompliance[]> {
  const db = await getComplianceDb();
  return (await db.getAll('compliance'))
    .map(normalizeCompliance)
    .filter((c) => !c.deleted_at)
    .sort((a, b) => {
      const ae = a.expiry_date ?? '9999-99-99';
      const be = b.expiry_date ?? '9999-99-99';
      const byExp = ae.localeCompare(be);
      if (byExp !== 0) return byExp;
      return a.title.localeCompare(b.title);
    });
}

export async function listComplianceBySync(
  state: SyncState,
): Promise<LocalCompliance[]> {
  const db = await getComplianceDb();
  return (await db.getAllFromIndex('compliance', 'by_sync', state)).map(
    normalizeCompliance,
  );
}

export async function countCompliancePending(): Promise<number> {
  const pending = await listComplianceBySync('pending');
  const syncing = await listComplianceBySync('syncing');
  return pending.length + syncing.length;
}

/** Merge server snapshot; never overwrite rows with pending local ops. */
export async function mergeComplianceFromServer(
  remote: Array<Partial<LocalCompliance> & { id: string }>,
): Promise<void> {
  const db = await getComplianceDb();
  const existing = await db.getAll('compliance');
  const byId = new Map(existing.map((r) => [r.id, normalizeCompliance(r)]));
  const now = new Date().toISOString();

  for (const r of remote) {
    const local = byId.get(r.id);
    if (local && local.syncState !== 'synced' && local.pendingOp) continue;
    const next: LocalCompliance = normalizeCompliance({
      id: r.id,
      category: (r.category as LocalCompliance['category']) ?? 'other',
      title: r.title ?? 'Untitled',
      subject_reference: r.subject_reference ?? null,
      issuer: r.issuer ?? null,
      issued_date: dateOnly(r.issued_date),
      expiry_date: dateOnly(r.expiry_date),
      lead_time_days: r.lead_time_days ?? 30,
      cost_pence: r.cost_pence ?? null,
      billing_frequency: r.billing_frequency ?? 'annual',
      notes: r.notes ?? null,
      document_reference: r.document_reference ?? null,
      active: r.active !== false,
      deleted_at: null,
      syncState: 'synced',
      pendingOp: null,
      renewNewExpiry: null,
      failReason: null,
      updatedAt: now,
    });
    await db.put('compliance', next);
    byId.set(r.id, next);
  }
}

export async function deleteComplianceLocal(id: string): Promise<void> {
  const db = await getComplianceDb();
  await db.delete('compliance', id);
}

export async function __resetComplianceDbForTests(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise;
    db.close();
    dbPromise = null;
  }
  await new Promise<void>((resolve, reject) => {
    const req = indexedDB.deleteDatabase('terratech-compliance');
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
}
